/** AdminRefunds - platform-wide refund browser with search, pagination, CSV. */
import React, { useEffect, useState, useCallback } from 'react';
import { View, Text, StyleSheet, FlatList, ActivityIndicator } from 'react-native';

import { Ionicons } from '@expo/vector-icons';
import { useLocalSearchParams } from 'expo-router';
import { api } from '../../src/api/client';
import { AdminToolbar } from '../../src/components/AdminList';
import ContentContainer from '../../src/components/ContentContainer';
import PageFooter from '../../src/components/PageFooter';
import { useTheme } from '../../src/context/ThemeContext';
import { spacing, fontSize, fontWeight } from '../../src/utils/theme';
import type { ColorTheme } from '../../src/utils/theme';
import { titleCase } from '../../src/utils/format';

interface RefundItem {
  id: string;
  amount: number;
  /** 'stripe' | 'credit_restore' (1.4 P01). */
  kind?: string;
  sourceKind?: string | null;
  restoredQuantity?: number | null;
  restoredUnit?: string | null;
  reason: string | null;
  status: string;
  createdAt: string;
  ownerName: string | null;
  refundedByName: string | null;
  customerName: string | null;
}

export default function AdminRefunds() {
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const [rows, setRows] = useState<RefundItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');

  const { ownerId: paramOwnerId } = useLocalSearchParams<{ ownerId?: string }>();
  const ownerFilter = typeof paramOwnerId === 'string' ? paramOwnerId : '';
  const [ownerFilterName, setOwnerFilterName] = useState('');

  const fetchRows = useCallback(async (p = 1, s = '', signal?: AbortSignal) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(p), limit: '20' });
      if (s) params.set('search', s);
      if (ownerFilter) params.set('ownerId', ownerFilter);
      const data = await api<{ refunds: RefundItem[]; total: number }>(`/api/admin/refunds?${params}`);
      if (signal?.aborted) return;
      setRows(data.refunds);
      setTotal(data.total);
      setPage(p);
      if (ownerFilter) setOwnerFilterName(data.refunds.find(r => (r as any).ownerName)?.ownerName ?? '');
    } catch (err) { if (!(signal?.aborted)) console.error(err); }
    finally { if (!(signal?.aborted)) setLoading(false); }
  }, [ownerFilter]);

  useEffect(() => { const ac = new AbortController(); fetchRows(1, '', ac.signal); return () => ac.abort(); }, [fetchRows]);

  const statusColor = (s: string) =>
    s === 'succeeded' ? colors.success : s === 'pending' ? colors.warning : colors.danger;

  return (
    <View style={styles.container}>
      <ContentContainer role="admin" footer={false}>
        {ownerFilter !== '' && (
          <View style={{ flexDirection: 'row', alignItems: 'center', alignSelf: 'flex-start', gap: 6, backgroundColor: colors.primary + '15', borderWidth: 1, borderColor: colors.primary, borderRadius: 16, paddingHorizontal: 12, paddingVertical: 5, marginBottom: 10 }}>
            <Ionicons name="business" size={14} color={colors.primary} />
            <Text style={{ color: colors.primary, fontSize: 13, fontWeight: '600' }}>
              {ownerFilterName ? `Owner: ${ownerFilterName}` : 'Filtered to one owner'}
            </Text>
          </View>
        )}
        <AdminToolbar
          search={search} onSearchChange={setSearch} onSearch={() => fetchRows(1, search)}
          total={total} page={page} limit={20} onPageChange={(p) => fetchRows(p, search)}
          csvEndpoint={`/api/admin/refunds?${new URLSearchParams({ ...(search && { search }), ...(ownerFilter && { ownerId: ownerFilter }) })}`}
          csvFilename="refunds.csv" label="refund"
        />
        {loading ? (
          <ActivityIndicator size="large" color={colors.primary} style={{ marginTop: spacing.xl }} />
        ) : (
          <FlatList ListFooterComponent={<PageFooter />}
            data={rows}
            keyExtractor={item => item.id}
            renderItem={({ item }) => (
              <View style={styles.row}>
                <View style={{ flex: 1 }}>
                  <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Text style={styles.amount}>
                      {item.kind === 'credit_restore'
                        ? `Restored ${item.restoredUnit === 'cents' ? `$${((item.restoredQuantity ?? 0) / 100).toFixed(2)}` : `${item.restoredQuantity ?? 0} ${item.restoredUnit ?? ''}`}`.trim()
                        : `-$${(item.amount / 100).toFixed(2)}`}
                    </Text>
                    <Text style={[styles.badge, { color: statusColor(item.status) }]}>{item.status}</Text>
                  </View>
                  <Text style={styles.meta} numberOfLines={1}>
                    {item.ownerName || '?'} · {titleCase(item.customerName) || 'Unknown customer'}
                  </Text>
                  <Text style={styles.sub} numberOfLines={2}>
                    {item.refundedByName ? `By ${titleCase(item.refundedByName)}` : 'By owner/admin'} · {item.reason || 'No reason'} · {new Date(item.createdAt).toLocaleString()}
                  </Text>
                </View>
              </View>
            )}
            ListEmptyComponent={<Text style={styles.empty}>No refunds found</Text>}
          />
        )}
      </ContentContainer>
    </View>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background, padding: spacing.md },
  row: { flexDirection: 'row', paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: c.borderLight },
  amount: { fontSize: fontSize.md, fontWeight: fontWeight.bold, color: c.danger },
  badge: { fontSize: fontSize.xs, fontWeight: fontWeight.semibold },
  meta: { fontSize: fontSize.xs, color: c.text, marginTop: 3, fontWeight: fontWeight.medium },
  sub: { fontSize: fontSize.xs, color: c.textSecondary, marginTop: 2 },
  empty: { textAlign: 'center', color: c.textSecondary, marginTop: spacing.xl, fontSize: fontSize.md },
});
const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
